import { type IService } from '../../shared';
import { type ServiceManager } from '../service-manager';
import {
    type FormatBrushApplyContext,
    type FormatBrushApplyResult,
    type FormatBrushState,
    type StartFormatBrushOptions,
} from './types';

const FORMAT_BRUSH_CHANGE_EVENT = 'format-brush:change';

type FormatBrushSession = FormatBrushState & {
    apply: (targetLayerId: string, context: FormatBrushApplyContext) => Promise<FormatBrushApplyResult>;
    dispose: () => void;
    applying: boolean;
    disposed: boolean;
    revision: number;
};

type DisplacementLike = {
    set(info: { type: 'custom'; x: number; y: number; txt: string }): void;
    clear(): void;
};

/**
 * 格式刷交互服务。
 *
 * 服务只负责模式状态、目标校验、串行应用、光标提示和生命周期清理；
 * 文字与图片的具体格式数据仍由对应图层领域模块持有。
 */
export class FormatBrushService implements IService {
    private session: FormatBrushSession | null = null;
    private state: FormatBrushState | null = null;
    private hoverTarget: HTMLElement | null = null;
    private hoverTargetCursor = '';
    private hoverTargetPointerEvents = '';
    private editorContainer: HTMLElement | null = null;
    private editorContainerCursor = '';
    private mouseMoveHandler: ((event: MouseEvent) => void) | null = null;
    private revision = 0;

    constructor(readonly service: ServiceManager) {}

    onInit() {
        this.cancel();
    }

    onBindEvent() {
        this.service.hotkey.onEsc(() => this.cancel(), 99);
        this.service.editor.onDisableOperateChange(() => this.cancel(), 99);
        this.service.editor.onDisablePickChange(() => this.cancel(), 99);
    }

    onUnmount() {
        this.cancel();
    }

    /** 启动新的格式刷；源图层或快照无效时保留当前 session。 */
    start<TData>(options: StartFormatBrushOptions<TData>) {
        const { implementation } = options;
        const sourceLayer = this.service.flatLayer.getLayer(options.sourceLayerId);
        if (!sourceLayer || sourceLayer.layerType !== implementation.targetLayerType) return false;

        let data: TData | undefined;
        try {
            data = implementation.extract(options.sourceLayerId);
        } catch (error) {
            this.service.captureException(error instanceof Error ? error : new Error(String(error)), {
                extra: {
                    sourceLayerId: options.sourceLayerId,
                    formatBrushTargetLayerType: implementation.targetLayerType,
                    phase: 'extract',
                },
            });
            return false;
        }
        if (data === undefined) return false;

        this.cancel();

        this.session = {
            sourceLayerId: options.sourceLayerId,
            targetLayerType: implementation.targetLayerType,
            cursorTip: options.cursorTip,
            apply: (targetLayerId, context) => implementation.apply(targetLayerId, context, data),
            dispose: () => implementation.dispose(data),
            applying: false,
            disposed: false,
            revision: ++this.revision,
        };
        this.state = {
            sourceLayerId: options.sourceLayerId,
            targetLayerType: implementation.targetLayerType,
            cursorTip: options.cursorTip,
        };
        this.setEditorContainerCursor('');
        this.emitChange();
        return true;
    }

    /** 获取当前只读状态快照。 */
    getState() {
        return this.state;
    }

    /** 监听格式刷进入和退出。 */
    onChange(listener: (state: FormatBrushState | null) => void, weight = 0) {
        const handler = this.service.events.on(FORMAT_BRUSH_CHANGE_EVENT, listener, weight);
        return () => this.service.events.off(handler);
    }

    /** 检查目标是否存在、不是源图层且类型匹配。 */
    canApply(layerId: string) {
        if (!layerId || !this.session || layerId === this.session.sourceLayerId) return false;
        return this.service.flatLayer.getLayer(layerId)?.layerType === this.session.targetLayerType;
    }

    /** 获取目标图层当前应展示的格式刷光标。 */
    getCursor(layerId: string): 'var(--cursor-brush)' | 'not-allowed' {
        return this.canApply(layerId) ? 'var(--cursor-brush)' : 'not-allowed';
    }

    /** 处理格式刷模式下的左键按下；无效或空目标会直接退出格式刷。 */
    async pointerDown(layerId: string, context: FormatBrushApplyContext) {
        if (!this.session) return false;
        if (!this.canApply(layerId)) {
            this.cancel();
            return false;
        }
        this.clearHover();
        this.service.frame.setHoverFrameId(null);
        this.service.frame.delHoverId();
        return this.apply(layerId, context);
    }

    /**
     * 串行应用格式刷。
     * 普通应用先关闭交互、Promise settled 后释放缓存；连续应用保留当前 session。
     */
    async apply(layerId: string, context: FormatBrushApplyContext) {
        const session = this.session;
        if (!session || session.applying || !this.canApply(layerId)) return false;

        session.applying = true;
        if (!context.continuous) this.deactivateForApply(session);

        try {
            const applied = await session.apply(layerId, context);
            if (applied && context.continuous && this.session === session) {
                const sourceLayer = this.service.flatLayer.getLayer(session.sourceLayerId);
                if (sourceLayer) this.service.frame.setActiveLayer(session.sourceLayerId);
            }
            if (applied && !context.continuous && this.revision === session.revision) {
                const targetLayer = this.service.flatLayer.getLayer(layerId);
                if (targetLayer) this.service.frame.setActiveLayer(layerId);
            }
            return applied;
        } catch (error) {
            this.service.captureException(error instanceof Error ? error : new Error(String(error)), {
                extra: {
                    sourceLayerId: session.sourceLayerId,
                    targetLayerId: layerId,
                    formatBrushTargetLayerType: session.targetLayerType,
                },
            });
            return false;
        } finally {
            session.applying = false;
            if (!context.continuous || this.session !== session) this.disposeSession(session);
        }
    }

    /** 根据坐标解析后的实际图层，为编辑器容器和当前 hover 图层设置光标。 */
    hover(target: HTMLElement | null, layerId: string) {
        this.clearHover();
        if (!this.session) return;

        this.setEditorContainerCursor(layerId);
        if (!target) return;

        this.hoverTarget = target;
        this.hoverTargetCursor = target.style.cursor;
        this.hoverTargetPointerEvents = target.style.pointerEvents;
        const cursor = this.getCursor(layerId);
        target.style.cursor = cursor;
        target.style.pointerEvents = 'all';

        if (cursor === 'not-allowed' || !this.session.cursorTip) return;

        const cursorTip = this.session.cursorTip;
        this.mouseMoveHandler = (event) => {
            this.getDisplacement()?.set({
                type: 'custom',
                x: event.x,
                y: event.y,
                txt: cursorTip,
            });
        };
        document.addEventListener('mousemove', this.mouseMoveHandler);
    }

    /** 清理 hover 光标和跟随提示，保留格式刷状态。 */
    clearHover() {
        const hasFormatBrushHover = this.hoverTarget !== null || this.mouseMoveHandler !== null;
        if (this.hoverTarget) {
            this.hoverTarget.style.cursor = this.hoverTargetCursor;
            this.hoverTarget.style.pointerEvents = this.hoverTargetPointerEvents;
        }
        this.hoverTarget = null;
        this.hoverTargetCursor = '';
        this.hoverTargetPointerEvents = '';
        if (this.mouseMoveHandler) document.removeEventListener('mousemove', this.mouseMoveHandler);
        this.mouseMoveHandler = null;
        if (hasFormatBrushHover) this.getDisplacement()?.clear();
        if (this.session) this.setEditorContainerCursor('');
    }

    /** 退出格式刷并立即释放当前 session 缓存。 */
    cancel() {
        const session = this.session;
        const hasState = this.state !== null;
        if (!session && !hasState && !this.hoverTarget && !this.mouseMoveHandler && !this.editorContainer) return;
        this.session = null;
        this.state = null;
        this.clearHover();
        this.restoreEditorContainerCursor();
        if (session) this.disposeSession(session);
        if (hasState) this.emitChange();
    }

    private deactivateForApply(session: FormatBrushSession) {
        if (this.session !== session) return;
        this.session = null;
        this.state = null;
        this.clearHover();
        this.restoreEditorContainerCursor();
        this.emitChange();
    }

    private disposeSession(session: FormatBrushSession) {
        if (session.disposed) return;
        session.disposed = true;
        try {
            session.dispose();
        } catch (error) {
            this.service.captureException(error instanceof Error ? error : new Error(String(error)), {
                extra: {
                    sourceLayerId: session.sourceLayerId,
                    formatBrushTargetLayerType: session.targetLayerType,
                    phase: 'dispose',
                },
            });
        }
    }

    private getDisplacement() {
        return this.service.registration.getInstance<DisplacementLike>('Displacement');
    }

    private setEditorContainerCursor(layerId: string) {
        const container = this.editorContainer ?? this.service.editor.getContainer();
        if (!container) return;
        if (!this.editorContainer) {
            this.editorContainer = container;
            this.editorContainerCursor = container.style.cursor;
        }
        container.style.cursor = this.getCursor(layerId);
    }

    private restoreEditorContainerCursor() {
        if (this.editorContainer) this.editorContainer.style.cursor = this.editorContainerCursor;
        this.editorContainer = null;
        this.editorContainerCursor = '';
    }

    private emitChange() {
        this.service.events.emit(FORMAT_BRUSH_CHANGE_EVENT, this.state);
    }
}
